// Zonas de detección: qué parte de la imagen de la cámara cuenta para la IA.
//
// El cliente dibuja polígonos sobre la foto: "detectar solo dentro de estas zonas" (incluir) y/o
// "ignorar estas zonas" (excluir). Cada zona puede aplicar a todo, solo a personas o solo a
// vehículos y máquinas. Los puntos se guardan normalizados (0–1): sobreviven a un cambio de resolución.
//
// REGLA "ADENTRO": se usa el punto de apoyo de la caja, el centro del borde inferior (pies de la
// persona, ruedas del vehículo), no el centro de la caja. Una persona en la vereda con la cabeza
// "dentro" de la obra no cuenta; una grúa o un camión alto dentro del predio sí.
//
// Misma lógica que el backend (src/zonas/services/zonas.ts), el motor de alertas del Editor y el
// patch de yolox (zonas-patch/). Si se cambia acá, cambiar allá.
import axios from 'axios';

export const APLICA = [
  { value: 'todo', label: 'Todo' },
  { value: 'personas', label: 'Personas' },
  { value: 'vehiculos', label: 'Vehículos y máquinas' },
];

const PERSONAS = new Set(['Persona con casco', 'Persona sin casco']);
export const familiaZona = (cls) => (PERSONAS.has(cls) ? 'personas' : 'vehiculos');

export const tieneZonas = (z) => !!z && ((z.incluir?.length || 0) + (z.excluir?.length || 0) > 0);

export function puntoEnPoligono(x, y, poly) {
  let dentro = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) dentro = !dentro;
  }
  return dentro;
}

const aplicaA = (z, fam) => !z.aplica || z.aplica === 'todo' || z.aplica === fam;

/** Punto de apoyo normalizado de una detección (centro del borde inferior). */
export const puntoApoyo = (d, w, h) => [((d.x1 + d.x2) / 2) / w, d.y2 / h];

/** ¿Cuenta la detección? Sin zonas o sin caja (fotos viejas, solo conteos) cuenta siempre. */
export function cuentaDeteccion(d, zonas, { w, h }) {
  if (!tieneZonas(zonas) || d.x1 == null || d.y2 == null || !(w > 0) || !(h > 0)) return true;
  const fam = familiaZona(d.cls);
  const [x, y] = puntoApoyo(d, w, h);
  const inc = (zonas.incluir || []).filter((z) => aplicaA(z, fam));
  if (inc.length && !inc.some((z) => puntoEnPoligono(x, y, z.puntos))) return false;
  if ((zonas.excluir || []).some((z) => aplicaA(z, fam) && puntoEnPoligono(x, y, z.puntos))) return false;
  return true;
}

/** Devuelve solo las detecciones que cuentan. dets con {cls, x1, y1, x2, y2} en píxeles de una imagen w×h. */
export function filtrarDetecciones(dets, zonas, { w, h }) {
  if (!tieneZonas(zonas)) return dets;
  return dets.filter((d) => cuentaDeteccion(d, zonas, { w, h }));
}

/** Aplica las zonas a una foto normalizada de iaData (devuelve una copia con dets filtradas y `ignoradas`). */
export function aplicarZonasFoto(photo, zonas) {
  if (!tieneZonas(zonas) || !photo.hasBoxes) return photo;
  const dets = filtrarDetecciones(photo.dets, zonas, photo);
  return dets.length === photo.dets.length ? photo : { ...photo, dets, ignoradas: photo.dets.length - dets.length };
}

// ---------------------------------------------------------------------------
// API (/api/zonas). Instancia propia de axios: un 403 de este módulo no cierra la sesión.
// En local se apunta a un backend propio con REACT_APP_ZONAS_API_URL.
// ---------------------------------------------------------------------------
const BASE = process.env.REACT_APP_ZONAS_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8081';
const api = axios.create({ baseURL: `${BASE.replace(/\/$/, '')}/api/zonas`, timeout: 15000 });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authLisualToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const errorZonas = (e) => e?.response?.data?.message || e?.message || 'Algo salió mal';

const cache = new Map(); // feedId -> Promise<doc|null>
export function getZonas(feedId, { fresco = false } = {}) {
  if (!feedId) return Promise.resolve(null);
  if (fresco || !cache.has(feedId)) {
    const p = api.get(`/${encodeURIComponent(feedId)}`).then((r) => r.data).catch((e) => { cache.delete(feedId); throw e; });
    cache.set(feedId, p);
  }
  return cache.get(feedId);
}
/** Para los paneles: si el servicio no responde (o tarda más de 5 s), se muestra todo como antes en vez de trabar la carga. */
export const getZonasSeguro = (feedId) => Promise.race([getZonas(feedId).catch(() => null), new Promise((r) => setTimeout(() => r(null), 5000))]);

export async function guardarZonas(feedId, { incluir, excluir }) {
  const r = await api.put(`/${encodeURIComponent(feedId)}`, { incluir, excluir });
  cache.set(feedId, Promise.resolve(r.data));
  return r.data;
}
